/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  ConsentExpiration,
  Policies,
  SubscriptionStatusEnum,
} from "@fena/toolkit-types";
import { useEffect } from "react";
import { ProSidebarProvider } from "react-pro-sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AppRoutes } from "../../constants/routes";
import { usePolicyCheck } from "../../modules/private/hooks/usePolicyCheck";
import { SetupWholesaleTagsModal } from "../../modules/private/settings-module/integrations/apps/components/modals/SetupWholesaleTagsModal";
import { useGetExternalAppList } from "../../modules/private/settings-module/integrations/apps/hooks/useGetExternalAppList";
import { useCompanyStore } from "../../stores/useCompanyStore";
import { useModalManager } from "../../context/ModalManager";
import { usePoliciesContext } from "../../context/PoliciesContextProvider";
import { useAuthCheck } from "../../hooks/useAuthCheck";
import { useSubscriptionCheck } from "../../modules/private/hooks/useSubscriptionCheck";
import Header from "../header";
import { LoadingBlock } from "../loading-block/LoadingBlock";
import { SideMenu, Typography, TypographyVariant } from "../ui-kit";
import SubscriptionWarningModal from "../ui-kit/modal/SubscriptionWarningModal";
import { SubscriptionOptionEnum } from "../../modules/private/settings-module/billing-and-packages/shared/types";
import { useUserContext } from "../../context/UserContextProvider";
import DisableContent from "./DisableContent";
import MissingDataBanner from "../ui-kit/missing-data-banner";
import { useMenuItems } from "./hooks/useMenuItems";
import { usePartnerLayoutContext } from "../../context/PartnerLayoutContextProvider";
import api from "../../util/api";
import {
  NotificationTypes,
  useNotification,
} from "../../hooks/useNotification";
import { useDialogManager } from "../../context/DialogManager";
import UpdatePaymentMethodModal from "../../modules/private/settings-module/billing-and-packages/components/modals/UpdatePaymentMethodModal";
import { getAppVersion } from "../../config/utils";
import { useShowBlockingOverlay } from "./hooks/useShowBlockingOverlay";

const Wrapper = styled.div`
  display: flex;
  height: 100vh;
  flex-direction: column;
`;

const SideMenuAndContent = styled.div<{ isB2bSubInactive?: boolean }>`
  display: flex;
  height: calc(100vh - 76px);
  margin-top: ${({ isB2bSubInactive }) =>
    isB2bSubInactive ? "131px" : "76px"};
  position: relative;
`;

const Div = styled.div<{
  hasNoSubscription?: boolean;
  hasB2BSubscription?: boolean;
}>`
  position: relative;
  background-color: #f4f5f9;
  flex: 1;
  overflow: ${(props) =>
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    props.hasNoSubscription || props.hasB2BSubscription ? "hidden" : "auto"};
`;

const Content = styled.div`
  flex: 1;
  background-color: #f4f5f9;
  padding: 30px;
  @media (max-width: 1180px) {
    padding: 20px 15px 15px 15px;
  }

  @media only screen and (max-width: 576px) {
    padding: 15px;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
`;

const Version = styled(Typography)`
  position: absolute;
  bottom: 0;
  left: 10px;
  z-index: 1;
  color: #8181a5cb;
  font-size: 8px;
`;

export const PrivateLayout = () => {
  const { policies } = usePoliciesContext();
  const { showNotification } = useNotification();
  const { showDialog } = useDialogManager();
  const { removeLastModal } = useModalManager();
  const { checkAccessByPolicies } = usePolicyCheck();
  const { data: externalApps } = useGetExternalAppList();
  const { checkSubscription } = useSubscriptionCheck();
  const { company, getStripeDetails, refreshStripeDetails } = useCompanyStore();
  const { user } = useUserContext();

  const { addModal, modals } = useModalManager();

  const location = useLocation();

  const handleUpdate = () => {
    removeLastModal();
    addModal(<UpdatePaymentMethodModal refresh={() => {}} />);
  };

  const onRetryConfirm = async () => {
    try {
      await api.retryPayment();
      showNotification(
        "Retried payment successfully",
        NotificationTypes.SUCCESS
      );
      removeLastModal();
    } catch (e: any) {
      showNotification(
        e?.error?.message ?? "Something went wrong",
        NotificationTypes.DANGER
      );
      console.error(e);
    }
  };

  const handleRetry = () => {
    showDialog({
      title: "Subscription warning",
      content: `Are you sure you want to retry the transaction?`,
      actions: [
        {
          type: "outline",
          text: "Leave",
        },
        {
          type: "primary",
          text: "Continue",
          handler: onRetryConfirm,
        },
      ],
    });
  };

  useEffect(() => {
    checkSubscription();
  }, [location.pathname]);

  useEffect(() => {
    const foundAppWithoutWholesaleTags = externalApps?.find(
      (app) =>
        app.isActive &&
        app.isOrdersInitialSyncDone &&
        !(
          app.isInitialWholesaleOrderTagsConfigDone ||
          app.isInitialWholesaleOrderTagsConfigDone
        )
    );

    if (
      checkAccessByPolicies(
        [Policies.APPS.ADMIN_ACCESS],
        [Policies.APPS.MODIFY_APPS]
      ) &&
      company?.currentSubscription?.status !==
        SubscriptionStatusEnum.payment_failed &&
      !!foundAppWithoutWholesaleTags &&
      modals.length < 1
    ) {
      addModal(
        <SetupWholesaleTagsModal appData={foundAppWithoutWholesaleTags} />,
        false
      );
    }
  }, [company, externalApps]);

  useEffect(() => {
    if (
      company?.currentSubscription?.status ===
      SubscriptionStatusEnum.payment_failed
    ) {
      modals.length < 1 &&
        addModal(
          <SubscriptionWarningModal
            handleRetry={handleRetry}
            handleUpdate={handleUpdate}
          />
        );
    }
  }, [company]);

  const hasPaymentsOnlySubscription =
    company?.currentSubscription?.details?.subscriptionOption ===
      SubscriptionOptionEnum.PAYMENTS_ONLY &&
    company.currentSubscription.status !== SubscriptionStatusEnum.processing;

  const hasToolkitSubscription =
    company?.currentSubscription?.details?.subscriptionOption ===
      SubscriptionOptionEnum.TOOLKIT &&
    company.currentSubscription.status !== SubscriptionStatusEnum.processing;

  const hasActiveB2BSubscription =
    !!company?.b2bSubscription && company.b2bSubscription.status != null;

  const hasNoSubscription =
    !company?.currentSubscription && !company?.b2bSubscription;

  // ignoreSubscription if the user is managed by a partner
  const ignoreSubscriptionCheck: boolean = !!company?.managedBy;

  const { isPartnerMode } = usePartnerLayoutContext();

  const { showBlockingOverlay } = useShowBlockingOverlay(
    hasPaymentsOnlySubscription,
    company?.canManageCompanies,
    hasActiveB2BSubscription,
    hasToolkitSubscription,
    company?.b2bSubscription?.subscriptionLevel
  );

  const {
    menuItems,
    settingsMenuItems,
    partnerMenuItems,
    partnerSettingsMenuItems,
  } = useMenuItems(
    hasPaymentsOnlySubscription,
    hasNoSubscription,
    policies,
    ignoreSubscriptionCheck,
    hasActiveB2BSubscription,
    hasToolkitSubscription,
    company?.canManageCompanies,
    getStripeDetails?.cardPaymentsEnabled
  );

  const navigate = useNavigate();
  const { checkSignInStatus, loading } = useAuthCheck();
  const asyncCheck = async () => {
    const { user } = await checkSignInStatus();
    if (
      !user &&
      !location.pathname.includes(AppRoutes.Open.Auth.SIGN_UP_START)
    ) {
      navigate(AppRoutes.Open.Auth.SIGN_IN_START);
    }
    refreshStripeDetails();
  };

  useEffect(() => {
    asyncCheck();
  }, []);

  const handleIsSideMenuHidden = () => {
    if (
      location.pathname.includes(
        AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST
      ) ||
      location.pathname.includes(
        AppRoutes.Private.Shipments.SHIPMENTS_PICKER_DETAILS
      )
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Wrapper>
      <Header />
      {company?.bankConsentExpired === ConsentExpiration.EXPIRED &&
        !company.canManageCompanies && <MissingDataBanner />}
      <LoadingBlock loading={loading}>
        <SideMenuAndContent
          isB2bSubInactive={
            !!company?.b2bSubscription &&
            company?.b2bSubscription?.status === null
          }
        >
          <ProSidebarProvider>
            <SideMenu
              isHidden={handleIsSideMenuHidden()}
              menuItems={!isPartnerMode ? menuItems : partnerMenuItems}
              settingsMenuItems={
                !isPartnerMode ? settingsMenuItems : partnerSettingsMenuItems
              }
              hasPaymentsOnlySubscription={hasPaymentsOnlySubscription}
              isManagedCompany={!!company?.managedBy}
              hasB2BSubscription={hasActiveB2BSubscription}
            />
          </ProSidebarProvider>
          <Div
            hasNoSubscription={
              hasNoSubscription &&
              !ignoreSubscriptionCheck &&
              showBlockingOverlay
            }
            hasB2BSubscription={
              hasActiveB2BSubscription &&
              !ignoreSubscriptionCheck &&
              showBlockingOverlay
            }
            id="scrollableDiv"
          >
            {showBlockingOverlay && !ignoreSubscriptionCheck && (
              <DisableContent
                userName={user?.firstName}
                hasPaymentsOnlySubscription={hasPaymentsOnlySubscription}
                hasB2BSubscription={hasActiveB2BSubscription}
              />
            )}
            <Content>
              <Outlet />
            </Content>
          </Div>
          <Version variant={TypographyVariant.BODY13}>
            v.{getAppVersion()}
          </Version>
        </SideMenuAndContent>
      </LoadingBlock>
    </Wrapper>
  );
};
